/**
 * The effective date of a policy is LOCKED once the policy has become effective (#36450).
 *
 * Why: as soon as a policy becomes effective we include it in our reports — written premium, for
 * one — for the period containing that effective date, and those reports are published and relied
 * on. If the effective date is moved afterwards, the premium we already reported in one period
 * silently belongs to another: the report we published and the one we would produce today from the
 * same data disagree, with no transaction to explain the difference. Our numbers no longer tie out.
 *
 * The only way to give an in-force policy a different effective date is to CANCEL-REWRITE it: a flat
 * cancel with reason `CNRW` ("Policy Rewrite"), then "Rewrite Policy" (staff-appsync-be
 * lambdas/rewrite-policy), which books a brand-new policy as New Business on the new date and leaves
 * the original cancelled. That is two transactions, each reported in the period it happened, so the
 * books stay honest.
 *
 * This is deliberate, not a missing feature. Stakeholders occasionally ask to "just let staff move
 * the date"; the request looks like a small UI unlock, and it is not. No permission unlocks it — not
 * CanEditEffectiveDate (future-effective policies only), not CanBackDate. Enforced server-side in
 * policy-mod's `validateEvent` (both `previewPolicyChangePolicy` and a segment-0 `startDate` edit,
 * which is the same thing), rechecked when a saved preview is confirmed (the date may have arrived
 * while the preview sat open), and mirrored in staff-fe's policy-settings rule and date pickers. The
 * unit tests next to each of those exist to fail if the guard is removed. Docs:
 * docs/policy-effective-date-immutable.md.
 */
import { getTodayStr } from '@ourbranch/date-fns-helpers';

export const EFFECTIVE_DATE_LOCKED_ERROR_CODE = 'EFFECTIVE_DATE_LOCKED';

/** Fits under a narrow form field; the full message goes in a tooltip and in the errors. */
export const EFFECTIVE_DATE_LOCKED_HINT = 'Locked once effective — cancel & rewrite to change.';

export const EFFECTIVE_DATE_LOCKED_MESSAGE =
  'This policy has become effective, so its effective date cannot be changed: it has already been reported ' +
  '(written premium) on that date, and moving it would make our reports stop tying out. To give it a different ' +
  'effective date, cancel it with reason "Policy Rewrite" and use Rewrite Policy.';

/**
 * True once the policy's effective date has arrived — on the effective date itself and every day
 * after, whatever the policy's status (active, expired or cancelled: it was reported just the same).
 * `todayStr` defaults to the business date in the policy's state (never a UTC `new Date()`, which is
 * a day off near midnight ET); tests pass it explicitly.
 */
export function isEffectiveDateLocked(
  policy: { effectiveDate: string; state?: string | null },
  todayStr: string = getTodayStr(policy.state)
): boolean {
  return policy.effectiveDate <= todayStr;
}
